import { HStack, Text, VStack } from '@chakra-ui/react';
import { NotificationDatesRange } from '@components/cockpit/panels/notification/card/NotificationDatesRange';
import { useAppSelector } from '@hooks/redux.hooks';
import { selectSituationTime } from '@redux/situation/situation.selectors';
import { formatDurationFromTo } from '@utils/date.utils';
import { FormattedMessage } from 'react-intl';

import { AlertLevelEnum, NotificationTypeEnum, SegmentAlertLevelEnum } from '@/types/data/data.types';

interface NotificationTimesParams {
  startTime: string;
  endTime: string | null;
  lastDetectionTime: string | null;
  lastProcedureFirstAlertStartTime: string | null;
  lastProcedureClosingTime: string | null;
  type: NotificationTypeEnum;
  isArchived: boolean;
  isSearch: boolean;
  situationTime: string | number;
}

function getNotificationTimesAndDuration({
  startTime,
  endTime,
  lastDetectionTime,
  lastProcedureFirstAlertStartTime,
  lastProcedureClosingTime,
  type,
  isSearch,
  situationTime,
}: NotificationTimesParams): {
  displayedStartTime: string | null;
  displayedEndTime: string | null;
  formattedDuration: string | null;
} {
  let displayedStartTime: string | null = null;
  let displayedEndTime: string | null = null;
  let formattedDuration: string | null = null;

  if (type === NotificationTypeEnum.DRONE) {
    displayedStartTime = startTime;
    if (endTime) {
      displayedEndTime = lastDetectionTime;
    }

    if (displayedEndTime) {
      formattedDuration = formatDurationFromTo(startTime, displayedEndTime);
    } else {
      formattedDuration = formatDurationFromTo(startTime, situationTime);
    }
  } else if (lastProcedureFirstAlertStartTime) {
    displayedStartTime = lastProcedureFirstAlertStartTime;
    displayedEndTime = endTime;

    if (isSearch && lastProcedureClosingTime) {
      formattedDuration = formatDurationFromTo(lastProcedureFirstAlertStartTime, lastProcedureClosingTime);
    } else if (!isSearch) {
      formattedDuration = formatDurationFromTo(lastProcedureFirstAlertStartTime, endTime ?? situationTime);
    }
  }

  return { displayedStartTime, displayedEndTime, formattedDuration };
}

type Props = {
  startTime: string;
  endTime: string | null;
  lastDetectionTime: string | null;
  lastProcedureFirstAlertStartTime: string | null;
  lastProcedureClosingTime: string | null;
  type: NotificationTypeEnum;
  isArchived: boolean;
  worstAlertLevel: AlertLevelEnum | null;
  isSearch: boolean;
};

export default function NotificationCardDates({
  startTime,
  endTime,
  lastDetectionTime,
  lastProcedureFirstAlertStartTime,
  lastProcedureClosingTime,
  type,
  isArchived,
  worstAlertLevel,
  isSearch,
}: Readonly<Props>) {
  const situationTime = useAppSelector(selectSituationTime) ?? Date.now();

  const { displayedStartTime, displayedEndTime, formattedDuration } = getNotificationTimesAndDuration({
    startTime,
    endTime,
    lastDetectionTime,
    lastProcedureFirstAlertStartTime,
    lastProcedureClosingTime,
    type,
    isArchived,
    isSearch,
    situationTime,
  });

  if (!displayedStartTime) {
    return;
  }

  function getWidth() {
    if (isSearch) {
      return '230px';
    } else if (isArchived) {
      return '224px';
    }
    return '322px';
  }

  return (
    <VStack gap={0.5} paddingX={2} paddingTop={3} alignItems="start" width={getWidth()} height="100%" flexGrow={1}>
      {(type === NotificationTypeEnum.DRONE ||
        type === NotificationTypeEnum.SENSOR ||
        worstAlertLevel === SegmentAlertLevelEnum.PERIMETER_EFFRACTION) &&
        displayedStartTime && (
          <>
            <Text color="neutral.300">
              <FormattedMessage id="notification.dates.title" values={{ type }} />
            </Text>
            <NotificationDatesRange startTime={displayedStartTime} endTime={displayedEndTime} />
            {formattedDuration ? (
              <HStack gap={1} color="neutral.300">
                <FormattedMessage
                  id={
                    type === NotificationTypeEnum.DRONE
                      ? 'notification.dates.durationEndedDrone'
                      : 'notification.dates.durationEndedOther'
                  }
                />
                <Text color="neutral.white">{formattedDuration}</Text>
              </HStack>
            ) : (
              <Text color="neutral.300">
                <FormattedMessage
                  id={lastProcedureFirstAlertStartTime ? 'global.noInformation' : 'components.notification.noAlert'}
                />
              </Text>
            )}
          </>
        )}
    </VStack>
  );
}
